import { EmptyView } from "@/components/view";
import { useActionHandler } from "@/hooks";
import { useOriginalData } from "@/stores/data-processing";
import { useActiveDataflowId, useDataflowStore } from "@/stores/dataflow";
import { useView } from "@/stores/ui";
import { Center, Flex, Loader } from "@mantine/core";
import type { FirstDataRenderedEvent, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { DatasetInfoBar } from "./components/DatasetInfoBar";
import { useDatasetInfo } from "./hooks/useDatasetInfo";
import { useProcessedDataLoader } from "./hooks/useProcessedDataLoader";
import { useVisualData } from "./hooks/useVisualData";
import { createColumnDefs } from "./utils/createColumnDefs";
import { autoSizeColumns, useTableAutoSize } from "./utils/useTableAutoSize";
import "./WorkspaceContentTable.css";

export function WorkspaceContentTable() {
  const activeDataflowId = useActiveDataflowId();
  const view = useView();
  const originalData = useOriginalData(activeDataflowId || "");
  const gridRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeDataflow = useDataflowStore((state) =>
    activeDataflowId ? state.dataflows[activeDataflowId] : undefined
  );

  const hasInputTransformer = activeDataflow?.flow_definition_json?.some(
    (t) => t.type === "user_file" || t.type === "data_input"
  );

  const visualData = useVisualData({
    activeDataflowId,
    originalData,
  });

  useProcessedDataLoader(
    activeDataflowId,
    activeDataflow,
    hasInputTransformer || false
  );

  const { colDefs, lastColumnField } = useMemo(() => {
    if (!visualData || !visualData.data.length) {
      return { colDefs: [], lastColumnField: null };
    }

    const { colDefs, lastColumnField } = createColumnDefs(
      visualData.metadata.columns
    );
    return {
      colDefs,
      lastColumnField,
      rowData: visualData.data,
    };
  }, [visualData]);

  const rowData = useMemo(() => {
    return visualData?.data || [];
  }, [visualData]);

  const gridKey = useMemo(() => {
    return `${activeDataflowId}-${view}-${
      visualData ? visualData.data.length : 0
    }`;
  }, [activeDataflowId, view, visualData]);

  const datasetInfo = useDatasetInfo(activeDataflow, visualData);

  useTableAutoSize(gridRef, containerRef, view, lastColumnField);

  const actionHandler = useActionHandler();

  if (!activeDataflowId || !hasInputTransformer) {
    return (
      <EmptyView
        title="No dataset selected"
        description="Select a dataset from the sidebar to view it in the table"
        height="60vh"
        button={{
          label: "Find dataset",
          onClick: () => actionHandler("datasets"),
        }}
      />
    );
  }

  function onGridReady(params: GridReadyEvent<any, any>) {
    gridRef.current = params;
    setTimeout(() => {
      autoSizeColumns(params.api, lastColumnField, view);
    }, 150);
  }

  function onFirstDataRendered(params: FirstDataRenderedEvent<any, any>) {
    setTimeout(() => {
      autoSizeColumns(params.api, lastColumnField, view);
    }, 150);
  }

  if (!visualData) {
    return (
      <Center h="100%" w="100%">
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <Flex
      ref={containerRef}
      className="workspace-content-table"
      h="calc(100vh - 150px)"
      w="100%"
      miw="100%"
      direction="column"
    >
      <Flex flex={1} mih={0} direction="column">
        <AgGridReact
          key={gridKey}
          ref={gridRef}
          rowData={rowData}
          columnDefs={colDefs}
          headerHeight={40}
          autoSizeStrategy={{
            type: "fitCellContents",
          }}
          defaultColDef={{
            minWidth: 150,
            resizable: true,
          }}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
        />
      </Flex>
      {datasetInfo && <DatasetInfoBar datasetInfo={datasetInfo} />}
    </Flex>
  );
}
